import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Save, Palette, MessageCircle, Send, Mail, Facebook, Instagram, Youtube, Building2 } from "lucide-react";

const GROUPS = [
  {
    title: "Platform", icon: Building2, fields: [
      ["platform_name", "Platform name", "text"],
      ["platform_subtitle", "Subtitle / tagline", "text"],
      ["footer_text", "Footer text", "textarea"],
      ["logo_url", "Logo URL", "text"],
      ["favicon_url", "Favicon URL", "text"],
      ["default_trainer_name", "Default trainer name", "text"],
      ["zoom_default_link", "Default Zoom link (used when scheduling new live classes)", "text"],
    ],
  },
  {
    title: "Community links", icon: MessageCircle, fields: [
      ["whatsapp_community", "WhatsApp community", "text"],
      ["telegram_link", "Telegram group", "text"],
      ["facebook_group", "Facebook group", "text"],
      ["instagram_link", "Instagram", "text"],
      ["youtube_channel", "YouTube channel", "text"],
    ],
  },
  {
    title: "Support", icon: Mail, fields: [
      ["support_email", "Support email", "email"],
      ["support_whatsapp", "Support WhatsApp (wa.me/…)", "text"],
    ],
  },
  {
    title: "Colors", icon: Palette, fields: [
      ["primary_color", "Primary color", "color"],
      ["secondary_color", "Secondary color", "color"],
    ],
  },
];

function SettingField({ fieldKey, label, type, value, onChange }) {
  const testId = `settings-${fieldKey}`;
  const safeValue = value || "";

  if (type === "textarea") {
    return (
      <Textarea
        data-testid={testId}
        value={safeValue}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 rounded-xl min-h-[80px]"
      />
    );
  }
  if (type === "color") {
    return (
      <div className="mt-1.5 flex items-center gap-2">
        <input
          type="color"
          value={safeValue || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 w-16 rounded-xl border border-border bg-transparent p-1"
          aria-label={label}
        />
        <Input
          data-testid={testId}
          value={safeValue}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-xl h-11 font-mono text-xs"
        />
      </div>
    );
  }
  return (
    <Input
      data-testid={testId}
      type={type}
      value={safeValue}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 rounded-xl h-11"
    />
  );
}

export default function AdminSettings() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  function upd(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function save() {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("✓ Changes Saved Successfully");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-settings">
      <p className="text-sm text-muted-foreground">
        Everything below is editable — no code changes needed. Community & support links reflect on the student dashboard within a few seconds (auto-refresh).
      </p>

      {GROUPS.map((g) => (
        <div key={g.title} className="rounded-3xl bg-card border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <g.icon className="h-5 w-5 text-primary" />
            <h3 className="font-display text-xl font-bold">{g.title}</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {g.fields.map(([key, label, type]) => (
              <div key={key} className={type === "textarea" ? "sm:col-span-2" : ""}>
                <Label>{label}</Label>
                <SettingField
                  fieldKey={key}
                  label={label}
                  type={type}
                  value={form[key]}
                  onChange={(v) => upd(key, v)}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="sticky bottom-4 z-10">
        <Button onClick={save} disabled={saving} data-testid="settings-save"
          className="w-full sm:w-auto rounded-full royal-gradient border-0 text-white btn-lift h-12 px-8 shadow-2xl">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving..." : "Save all changes"}
        </Button>
      </div>
    </div>
  );
}
